import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, switchMap } from 'rxjs';
import { LOCAL_STORAGE_DEFAULTS, LocalStorageConfig, LocalStorageItemDto } from '@suite/local-storage-api';
import {
  LocalStorageClearRequest,
  LocalStorageItem,
  LocalStorageRemoveRequest,
  LocalStorageSetRequest,
} from '@suite/local-storage-models';

/**
 * Local storage service that provides chunked storage capabilities with encoding/decoding.
 *
 * This service automatically handles:
 * - Value chunking to avoid browser localStorage size limits
 * - Base64 encoding/decoding of complex objects
 * - Prefixed keys to avoid conflicts with other applications
 * - Timestamp metadata for when items were stored
 * - Fallback to in-memory storage if localStorage is unavailable
 *  - Backward compatibility with items stored without metadata
 *
 * All values are stored as encoded strings with metadata and automatically decoded when retrieved.
 * The main API always returns LocalStorageItem objects containing both the value and metadata.
 */
@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  /** Configuration for storage behavior (prefix, chunk size, etc.) */
  private readonly config: LocalStorageConfig = {
    prefix: LOCAL_STORAGE_DEFAULTS.PREFIX,
    chunkSize: LOCAL_STORAGE_DEFAULTS.CHUNK_SIZE,
    enableCompression: LOCAL_STORAGE_DEFAULTS.ENABLE_COMPRESSION,
  };

  /** Cached storage instance (localStorage or fallback memory storage) */
  private storage?: Storage;

  setItem$ = new Subject<LocalStorageSetRequest>();
  removeValue$ = new Subject<LocalStorageRemoveRequest>();
  clearStorage$ = new Subject<LocalStorageClearRequest>();

  constructor() {
    this.setItem$
      .pipe(
        switchMap((request) => {
          try {
            this.setItemSync(request.key, request.value);
            request.onSuccess?.();
            return EMPTY;
          } catch (error) {
            request.onError?.(error);
            return EMPTY;
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.removeValue$
      .pipe(
        switchMap((request) => {
          try {
            this.removeItemSync(request.key);
            request.onSuccess?.();
            return EMPTY;
          } catch (error) {
            request.onError?.(error);
            return EMPTY;
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.clearStorage$
      .pipe(
        switchMap((request) => {
          try {
            this.clearSync();
            request.onSuccess?.();
            return EMPTY;
          } catch (error) {
            request.onError?.(error);
            return EMPTY;
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  /**
   * Gets an item from storage by key.
   *
   * @param key - The storage key (without prefix)
   * @returns LocalStorageItem with metadata or undefined if not found
   */
  getItem(key: string): LocalStorageItem | undefined {
    try {
      return this.getItemSync(key);
    } catch {
      return undefined;
    }
  }

  /**
   * Gets the decoded value from an item, with optional default value.
   *
   * @param key - The storage key (without prefix)
   * @param defaultValue - Value to return if key doesn't exist or error occurs
   * @returns The decoded value or defaultValue
   */
  getValue(key: string, defaultValue?: unknown): unknown {
    try {
      const item = this.getItemSync(key);
      return item ? item.value : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  /**
   * Checks if a key exists in storage.
   *
   * @param key - The storage key (without prefix)
   * @returns true if key exists, false otherwise
   */
  hasItem(key: string): boolean {
    try {
      const prefixedKey = this.getPrefixedKey(key);
      return this.collectKeys().includes(prefixedKey);
    } catch {
      return false;
    }
  }

  /**
   * Gets all keys in storage, optionally filtered by pattern.
   *
   * Returns only keys for this service (filtered by prefix) with the prefix removed.
   *
   * @param pattern - Optional string pattern to filter keys
   * @returns Array of unprefixed keys
   */
  getKeys(pattern?: string): string[] {
    try {
      const keys = this.collectKeys();
      const prefix = this.config.prefix;

      // Remove prefix and filter by pattern if provided
      return keys
        .filter((key) => key.startsWith(prefix))
        .map((key) => key.substring(prefix.length + 1)) // +1 for separator ':'
        .filter((key) => !pattern || key.includes(pattern));
    } catch {
      return [];
    }
  }

  /**
   * Gets the number of items stored by this service.
   *
   * Only counts items with the configured prefix.
   *
   * @returns Number of stored items
   */
  getSize(): number {
    try {
      return this.collectKeys().length;
    } catch {
      return 0;
    }
  }

  // Private implementation methods

  /**
   * Gets the storage instance (localStorage or memory fallback).
   *
   * Lazy-loads and caches the storage instance for performance.
   */
  private getStorage(): Storage {
    if (!this.storage) {
      this.storage = this.getLocalStorage();
    }
    return this.storage;
  }

  /**
   * Gets localStorage with fallback to memory storage.
   *
   * Tests localStorage availability (may be disabled in private browsing mode)
   * and creates an in-memory fallback if localStorage is not available.
   */
  private getLocalStorage(): Storage {
    try {
      const test = '__local_storage_test__';
      window.localStorage.setItem(test, 'test');
      window.localStorage.removeItem(test);
      return window.localStorage;
    } catch {
      // Fallback to in-memory storage if localStorage is not available
      return this.createMemoryStorage();
    }
  }

  /**
   * Creates an in-memory storage implementation that mimics the Storage interface.
   *
   * Used as fallback when localStorage is not available (e.g., private browsing mode).
   * Data will be lost when the page refreshes or closes.
   */
  private createMemoryStorage(): Storage {
    const store: Record<string, string> = {};

    return {
      getItem: (key: string) => store[key] || null,
      setItem: (key: string, value: string) => {
        store[key] = value;
      },
      removeItem: (key: string) => {
        delete store[key];
      },
      clear: () => {
        Object.keys(store).forEach((key) => delete store[key]);
      },
      get length() {
        return Object.keys(store).length;
      },
      key: (index: number) => Object.keys(store)[index] || null,
    };
  }

  /**
   * Synchronously gets an item and decodes it using LocalStorageItem.
   *
   * @param key - The unprefixed storage key
   * @returns The LocalStorageItem with metadata or undefined if not found
   */
  private getItemSync(key: string): LocalStorageItem | undefined {
    const prefixedKey = this.getPrefixedKey(key);

    // Collect distributed values from potentially multiple chunks
    const valueStr = this.collectValues(prefixedKey);
    if (!valueStr || valueStr === '{}') {
      return undefined;
    }

    // First decode to get the wrapper object
    const dto: LocalStorageItemDto = {
      key,
      value: valueStr,
      timestamp: Date.now(), // Temporary, will be replaced
    };

    const decodedItem = LocalStorageItem.fromDto(dto);
    const wrapper = decodedItem.value as any;

    // Check if this is the new wrapper format or legacy format
    if (wrapper && typeof wrapper === 'object' && 'value' in wrapper && 'timestamp' in wrapper) {
      // New format: has wrapper with value and timestamp
      // wrapper.value is already decoded, so create LocalStorageItem directly
      return {
        key,
        value: wrapper.value,
        timestamp: wrapper.timestamp,
      } as LocalStorageItem;
    } else {
      // Legacy format: no timestamp metadata, use current time
      return LocalStorageItem.fromDto({
        key,
        value: valueStr,
        timestamp: Date.now(),
      });
    }
  }

  /**
   * Encodes a value to the storage format (base64 encoded JSON).
   *
   * @param value - The value to encode
   * @returns The encoded string
   */
  private encodeValue(value: unknown): string {
    try {
      const str = JSON.stringify(value);
      const escaped = encodeURIComponent(str);
      return window.btoa(escaped) + LOCAL_STORAGE_DEFAULTS.EOF_MARKER;
    } catch {
      return String(value) + LOCAL_STORAGE_DEFAULTS.EOF_MARKER;
    }
  }

  /**
   * Synchronously sets an item using LocalStorageItem for encoding.
   *
   * Handles chunking automatically if the encoded value exceeds the chunk size.
   * Stores both the value and metadata (timestamp) together.
   *
   * @param key - The unprefixed storage key
   * @param value - The value to store (will be encoded)
   */
  private setItemSync(key: string, value: unknown): void {
    const prefixedKey = this.getPrefixedKey(key);

    // Clear existing chunks to avoid orphaned data
    this.clearChunks(prefixedKey);

    // Create a wrapper object that includes both value and metadata
    const timestamp = Date.now();
    const wrapper = { value, timestamp };

    // Encode the wrapper (value + metadata) as a single unit
    const item = { key, value: wrapper, timestamp };
    const dto = LocalStorageItem.toDto(item);

    // Store the encoded value (chunked if necessary)
    this.distributeValue(prefixedKey, dto.value || '');
  }

  /**
   * Synchronously removes an item and all its chunks.
   *
   * @param key - The unprefixed storage key
   */
  private removeItemSync(key: string): void {
    const prefixedKey = this.getPrefixedKey(key);
    this.clearChunks(prefixedKey);
  }

  /**
   * Clears all items with the configured prefix.
   *
   * Only removes items belonging to this service instance.
   */
  private clearSync(): void {
    const storage = this.getStorage();
    const prefix = this.config.prefix;
    const keysToRemove: string[] = [];

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => storage.removeItem(key));
  }

  /**
   * Adds the configured prefix to a key.
   *
   * Format: "prefix:key" (e.g., "app:noova-energy:userSettings")
   *
   * @param key - The unprefixed key
   * @returns The prefixed key
   */
  private getPrefixedKey(key: string): string {
    return `${this.config.prefix}:${key}`;
  }

  /**
   * Collects all unique base keys (excluding chunk suffixes) for this service.
   *
   * Filters by prefix and removes chunk indicators (underscores) to get base keys only.
   *
   * @returns Array of base keys with prefix
   */
  private collectKeys(): string[] {
    const storage = this.getStorage();
    const prefix = this.config.prefix;
    const keys: string[] = [];

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(prefix)) {
        // Only include base keys, not chunks (chunks have underscores)
        const baseKey = key.split('_')[0];
        if (!keys.includes(baseKey)) {
          keys.push(baseKey);
        }
      }
    }

    return keys;
  }

  /**
   * Collects and reassembles chunked values for a given base key.
   *
   * Finds all chunks (baseKey, baseKey_, baseKey__, etc.) and joins them in order.
   * The chunking strategy uses underscores to indicate chunk order.
   *
   * @param baseKey - The prefixed base key (without chunk suffixes)
   * @returns The reassembled value string
   */
  private collectValues(baseKey: string): string {
    const storage = this.getStorage();
    const chunks: string[] = [];
    const keys: string[] = [];

    // Find all chunks for this key
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(baseKey)) {
        keys.push(key);
      }
    }

    // Sort keys to ensure correct order (baseKey, baseKey_, baseKey__, etc.)
    keys.sort((a, b) => {
      const aChunks = a.split('_').length;
      const bChunks = b.split('_').length;
      return aChunks - bChunks;
    });

    // Collect values in order
    keys.forEach((key) => {
      const value = storage.getItem(key);
      if (value) {
        chunks.push(value);
      }
    });

    return chunks.join('');
  }

  /**
   * Removes all chunks for a given base key.
   *
   * Finds and removes the base key and all its chunk variants.
   *
   * @param baseKey - The prefixed base key (without chunk suffixes)
   */
  private clearChunks(baseKey: string): void {
    const storage = this.getStorage();
    const keysToRemove: string[] = [];

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(baseKey)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => storage.removeItem(key));
  }

  /**
   * Distributes a value string across multiple storage chunks if needed.
   *
   * Splits large values into chunks of configurable size (default 100KB) to avoid
   * browser localStorage size limits. Uses underscore suffixes to indicate chunk order:
   * - baseKey (first chunk)
   * - baseKey_ (second chunk)
   * - baseKey__ (third chunk), etc.
   *
   * @param baseKey - The prefixed base key
   * @param value - The encoded value string to store
   */
  private distributeValue(baseKey: string, value: string): void {
    const storage = this.getStorage();
    const chunkSize = this.config.chunkSize;
    const chunks = value.match(new RegExp(`.{1,${chunkSize}}`, 'g')) || [];

    chunks.forEach((chunk, index) => {
      const suffix = index === 0 ? '' : '_'.repeat(index);
      const key = `${baseKey}${suffix}`;
      storage.setItem(key, chunk);
    });
  }
}
